import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Upload,
  X,
  Image as ImageIcon,
  Pencil,
  Check,
  ChevronsUpDown,
  // Video, // TODO: uncomment when video upload is enabled
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ImageAnnotationModal } from "./ImageAnnotationModal";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import {
  updateConsultationActions,
  SaveAIChatMessage,
  // generateVideoUploadUrl, // TODO: uncomment when video upload is enabled
} from "@/dispatchers/patientDispatchers";
import { ActionType } from "../../../enum/ActionType";
import {
  setImageDocDetails,
  setIncrementImageDocCompleted,
  addPatientVitals,
  getPatientVitals,
  trackServiceBilling,
} from "@/dispatchers/patientDispatchers";
import { content } from "html2canvas/dist/types/css/property-descriptors/content";
import useTitanApp from "@/hooks/useTitanApp";

interface UploadImageModalProps {
  trigger: React.ReactNode;
  onSave?: (data: any) => void;
  onSaveVital?: (vital: any) => void;
  patientEmail?: string;
  patientName?: string;
  forcedBodyArea?: string;
  /** Optional — required for consultation mode */
  consultationId?: string;
  /** Optional — required for consultation mode */
  patientId?: string;
  /** "vitals" = skip consultation API, just log payload. "consultation" = existing behaviour (default) */
  mode?: "vitals" | "consultation";
}

const BODY_PART_OPTIONS = [
  { value: "face", label: "Face", category: "Head & Neck" },
  { value: "head", label: "Head", category: "Head & Neck" },
  { value: "scalp", label: "Scalp", category: "Head & Neck" },
  { value: "forehead", label: "Forehead", category: "Head & Neck" },
  { value: "eyes", label: "Eyes", category: "Head & Neck" },
  { value: "ears", label: "Ears", category: "Head & Neck" },
  { value: "nose", label: "Nose", category: "Head & Neck" },
  { value: "mouth", label: "Mouth", category: "Head & Neck" },
  { value: "lips", label: "Lips", category: "Head & Neck" },
  { value: "tongue", label: "Tongue", category: "Head & Neck" },
  { value: "teeth", label: "Teeth", category: "Head & Neck" },
  { value: "throat", label: "Throat", category: "Head & Neck" },
  { value: "neck", label: "Neck", category: "Head & Neck" },
  { value: "chest", label: "Chest", category: "Upper Body" },
  { value: "breast", label: "Breast", category: "Upper Body" },
  { value: "back-upper", label: "Upper Back", category: "Upper Body" },
  { value: "shoulders", label: "Shoulders", category: "Upper Body" },
  { value: "shoulder-left", label: "Left Shoulder", category: "Upper Body" },
  { value: "shoulder-right", label: "Right Shoulder", category: "Upper Body" },
  { value: "abdomen", label: "Abdomen", category: "Upper Body" },
  { value: "stomach", label: "Stomach", category: "Upper Body" },
  { value: "arms", label: "Arms", category: "Arms & Hands" },
  { value: "arm-left", label: "Left Arm", category: "Arms & Hands" },
  { value: "arm-right", label: "Right Arm", category: "Arms & Hands" },
  { value: "elbow-left", label: "Left Elbow", category: "Arms & Hands" },
  { value: "elbow-right", label: "Right Elbow", category: "Arms & Hands" },
  { value: "forearm-left", label: "Left Forearm", category: "Arms & Hands" },
  { value: "forearm-right", label: "Right Forearm", category: "Arms & Hands" },
  { value: "wrist-left", label: "Left Wrist", category: "Arms & Hands" },
  { value: "wrist-right", label: "Right Wrist", category: "Arms & Hands" },
  { value: "hands", label: "Hands", category: "Arms & Hands" },
  { value: "hand-left", label: "Left Hand", category: "Arms & Hands" },
  { value: "hand-right", label: "Right Hand", category: "Arms & Hands" },
  { value: "fingers", label: "Fingers", category: "Arms & Hands" },
  { value: "nails", label: "Nails", category: "Arms & Hands" },
  { value: "back-lower", label: "Lower Back", category: "Lower Body" },
  { value: "hips", label: "Hips", category: "Lower Body" },
  { value: "buttocks", label: "Buttocks", category: "Lower Body" },
  { value: "groin", label: "Groin", category: "Lower Body" },
  { value: "legs", label: "Legs", category: "Legs & Feet" },
  { value: "leg-left", label: "Left Leg", category: "Legs & Feet" },
  { value: "leg-right", label: "Right Leg", category: "Legs & Feet" },
  { value: "thigh-left", label: "Left Thigh", category: "Legs & Feet" },
  { value: "thigh-right", label: "Right Thigh", category: "Legs & Feet" },
  { value: "knee-left", label: "Left Knee", category: "Legs & Feet" },
  { value: "knee-right", label: "Right Knee", category: "Legs & Feet" },
  { value: "calf-left", label: "Left Calf", category: "Legs & Feet" },
  { value: "calf-right", label: "Right Calf", category: "Legs & Feet" },
  { value: "ankle-left", label: "Left Ankle", category: "Legs & Feet" },
  { value: "ankle-right", label: "Right Ankle", category: "Legs & Feet" },
  { value: "feet", label: "Feet", category: "Legs & Feet" },
  { value: "foot-left", label: "Left Foot", category: "Legs & Feet" },
  { value: "foot-right", label: "Right Foot", category: "Legs & Feet" },
  { value: "toes", label: "Toes", category: "Legs & Feet" },
  { value: "rash", label: "Rash", category: "Skin Conditions" },
  { value: "bruise", label: "Bruise", category: "Skin Conditions" },
  { value: "wound", label: "Wound", category: "Skin Conditions" },
  { value: "scar", label: "Scar", category: "Skin Conditions" },
  { value: "mole", label: "Mole", category: "Skin Conditions" },
  { value: "lesion", label: "Lesion", category: "Skin Conditions" },
  { value: "burn", label: "Burn", category: "Skin Conditions" },
  { value: "bite", label: "Bite/Sting", category: "Skin Conditions" },
  { value: "incision", label: "Incision", category: "Skin Conditions" },
  { value: "full-body", label: "Full Body", category: "Other" },
  { value: "other", label: "Other", category: "Other" },
];

const groupedBodyParts = BODY_PART_OPTIONS.reduce(
  (acc, option) => {
    if (!acc[option.category]) acc[option.category] = [];
    acc[option.category].push(option);
    return acc;
  },
  {} as Record<string, typeof BODY_PART_OPTIONS>,
);

export function UploadImageModal({
  trigger,
  onSave,
  onSaveVital,
  patientEmail = "",
  patientName = "Patient",
  forcedBodyArea,
  consultationId = "",
  patientId,
  mode = "consultation",
}: UploadImageModalProps) {
  const { patientState, appState } = useTitanApp();
  const { selectedPatientDetails } = patientState;
  const { companyId } = appState;

  const [open, setOpen] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageTitle, setImageTitle] = useState(forcedBodyArea || "");
  const [comboboxOpen, setComboboxOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [showAnnotation, setShowAnnotation] = useState(false);

  // TODO: Video state — uncomment when video upload is enabled
  // const [isVideo, setIsVideo] = useState(false);
  // const [isProcessingVideo, setIsProcessingVideo] = useState(false);

  const { toast } = useToast();
  // ── IMAGE: Compress to max 1600px JPEG ──────────────────────────────────────
  const compressImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const MAX_DIMENSION = 1600;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_DIMENSION) {
              height = (height * MAX_DIMENSION) / width;
              width = MAX_DIMENSION;
            }
          } else {
            if (height > MAX_DIMENSION) {
              width = (width * MAX_DIMENSION) / height;
              height = MAX_DIMENSION;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          if (ctx) {
            ctx.drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL("image/jpeg", 0.85));
          } else {
            reject(new Error("Could not get canvas context"));
          }
        };
        img.onerror = () => reject(new Error("Could not load image"));
        img.src = e.target?.result as string;
      };
      reader.onerror = () => reject(new Error("Could not read file"));
      reader.readAsDataURL(file);
    });
  };

  // TODO: Video thumbnail extraction — uncomment when video upload is enabled
  // const extractVideoThumbnail = (file: File): Promise<string> => { ... };

  const handleFileSelect = (file: File | null) => {
    if (!file) return;

    const isImageFile = file.type.startsWith("image/");
    // TODO: uncomment when video upload is enabled
    // const isVideoFile = file.type.startsWith("video/");

    if (!isImageFile) {
      toast({
        title: "Invalid file type",
        description: "Please select an image (JPG, PNG, WebP)",
        variant: "destructive",
      });
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast({
        title: "File too large",
        description: "Please select an image smaller than 5MB",
        variant: "destructive",
      });
      return;
    }

    setUploadedImage(file);

    // TODO: Video branch — uncomment when video upload is enabled
    // if (isVideoFile) { ... } else {
    compressImage(file)
      .then((compressedDataUrl) => {
        setImagePreview(compressedDataUrl);

        const originalSize = file.size;
        const compressedSize = Math.round((compressedDataUrl.length * 3) / 4);
        const compressionRatio = (
          ((originalSize - compressedSize) / originalSize) *
          100
        ).toFixed(0);

        if (parseInt(compressionRatio) > 10) {
          toast({
            title: "Image compressed",
            description: `Reduced size by ${compressionRatio}% for optimal storage`,
          });
        }
      })
      .catch(() => {
        toast({
          title: "Error processing image",
          description: "Could not compress image. Please try a different file.",
          variant: "destructive",
        });
      });
    // } // end video/image branch
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) handleFileSelect(files[0]);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleAnnotatedImageSave = (annotatedImageUrl: string) => {
    setImagePreview(annotatedImageUrl);
  };

  const handleUploadImage = async () => {
    if (!uploadedImage || !imagePreview) {
      toast({
        title: "No file selected",
        description: "Please select an image to upload",
        variant: "destructive",
      });
      return;
    }
    if (!imageTitle.trim()) {
      toast({
        title: "Body part required",
        description: "Please select a body part or area",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);

    try {
      const originalExt = uploadedImage.name.includes(".")
        ? uploadedImage.name.substring(uploadedImage.name.lastIndexOf("."))
        : ".jpeg";

      const base64Content = imagePreview.split(",")[1];
      const sizeInKB = Math.round((base64Content.length * 3) / 4 / 1024);

      if (mode === "vitals") {
        // ── VITALS MODE: skip consultation API, just log payload ──────────

        await addPatientVitals({
          PatientId: patientId,
          PatientName: patientName,
          CreatedOn: new Date().toISOString(),
          ImageVitals: [
            {
              BodyArea: imageTitle.trim(),
              FileName: `${imageTitle.trim()}${originalExt}`,
              FileExtension: originalExt,
              Value: base64Content,
              ContentType: uploadedImage!.type,
              CreatedOn: new Date().toISOString(),
            },
          ],
        });
        await getPatientVitals(patientId);
      } else {
        // ── CONSULTATION MODE: existing behaviour (unchanged) ─────────────
        // TODO: Video path — uncomment when video upload is enabled
        // if (isVideo) { ... } else {

        const imagePayload = {
          patientId: patientId,
          title: imageTitle.trim(),
          fileName: `${imageTitle.trim()}${originalExt}`,
          description: `Image: ${imageTitle.trim()}`,
          size: `${sizeInKB} KB`,
          status: 1,
          fileContent: base64Content,
          consultationId: consultationId,
          bodyArea: imageTitle.trim(),
          isVideo: false,
          recordedAt: new Date().toISOString(),
        };

        await updateConsultationActions({
          consultationId: consultationId,
          payload: {
            actionName: ActionType[13],
            actionNumber: "13",
            isPerformed: true,
            payload: JSON.stringify(imagePayload),
          },
        });
        await SaveAIChatMessage({
          ConsultationChat: JSON.stringify([
            {
              sender: "success",
              message: "Media uploaded successfully.",
              actionId: 13,
              details: imagePayload,
            },
          ]),
          ConsultationId: consultationId,
        });
        if (consultationId) {
          try {
            trackServiceBilling({
              companyId,
              consultationId,
              pharmacyId: selectedPatientDetails?.pharmacyId,
              actiontype: "image document",
            });
          } catch (err) {
            console.error("Error calling billing tracker for signature:", err);
          }
        }
        setImageDocDetails(imagePayload);
        setIncrementImageDocCompleted(true);

        // Also persist as an image vital so it appears on the vitals dashboard
        await addPatientVitals({
          PatientId: patientId,
          PatientName: patientName,
          CreatedOn: new Date().toISOString(),
          ImageVitals: [
            {
              BodyArea: imageTitle.trim(),
              FileName: `${imageTitle.trim()}${originalExt}`,
              FileExtension: originalExt,
              Value: base64Content,
              ContentType: uploadedImage!.type,
              CreatedOn: new Date().toISOString(),
            },
          ],
        });
        // } // end video/image branch
      }

      toast({
        title: "Image uploaded",
        description: `${imageTitle} saved successfully`,
      });

      if (onSave)
        onSave({
          title: imageTitle.trim(),
          imagePreview: imagePreview,
          fileName: `${imageTitle.trim()}${originalExt}`,
          isVideo: false,
          consultationId,
        });

      setOpen(false);
      resetForm();
    } catch (error) {
      console.error("Upload error:", error);
      toast({
        title: "Upload failed",
        description: "Please try again or contact support",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const resetForm = () => {
    setUploadedImage(null);
    setImagePreview(null);
    setImageTitle(forcedBodyArea || "");
    // TODO: uncomment when video upload is enabled
    // setIsVideo(false);
    // setIsProcessingVideo(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {forcedBodyArea
              ? `Upload Image - ${forcedBodyArea}`
              : "Upload Image"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Body Part Selection */}
          <div className="space-y-2">
            <Label htmlFor="body-part">
              Body Part / Area <span className="text-red-500">*</span>
              {forcedBodyArea && (
                <span className="ml-2 text-xs text-gray-500 font-normal">
                  (Locked)
                </span>
              )}
            </Label>
            <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={comboboxOpen}
                  className="w-full justify-between"
                  disabled={!!forcedBodyArea}
                >
                  {imageTitle || "Select body part or area..."}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search body parts..." />
                  <CommandList>
                    <CommandEmpty>No body part found.</CommandEmpty>
                    {Object.entries(groupedBodyParts).map(
                      ([category, options]) => (
                        <CommandGroup key={category} heading={category}>
                          {options.map((option) => (
                            <CommandItem
                              key={option.value}
                              value={option.label}
                              onSelect={(currentValue) => {
                                setImageTitle(
                                  currentValue === imageTitle
                                    ? ""
                                    : option.label,
                                );
                                setComboboxOpen(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  imageTitle === option.label
                                    ? "opacity-100"
                                    : "opacity-0",
                                )}
                              />
                              {option.label}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      ),
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Upload Area */}
          <div className="space-y-2">
            <Label>Select Image</Label>
            {!uploadedImage ? (
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  isDragActive
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-300 hover:border-gray-400"
                }`}
              >
                <ImageIcon className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p className="text-sm text-gray-600 mb-2">
                  Drag and drop here, or click to select
                </p>
                <input
                  type="file"
                  id="image-upload"
                  className="hidden"
                  onChange={(e) => {
                    const files = e.target.files;
                    if (files && files.length > 0) handleFileSelect(files[0]);
                  }}
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  // TODO: add video types when video upload is enabled
                  // accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,video/mp4,video/quicktime,video/webm,video/x-msvideo"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    document.getElementById("image-upload")?.click()
                  }
                >
                  Select File
                </Button>
                <p className="text-xs text-gray-500 mt-2">
                  Images: JPG, PNG, GIF, WebP — Max 5MB
                  {/* TODO: uncomment when video upload is enabled */}
                  {/* <br />Videos: MP4, MOV, WebM — Max 100MB */}
                </p>
              </div>
            ) : (
              imagePreview && (
                <div className="relative">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full h-48 object-cover rounded-lg"
                  />

                  {/* TODO: Video badge — uncomment when video upload is enabled */}
                  {/* {isVideo && ( ... )} */}

                  {/* TODO: Video processing overlay — uncomment when video upload is enabled */}
                  {/* {isProcessingVideo && ( ... )} */}

                  {/* Remove button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setUploadedImage(null);
                      setImagePreview(null);
                      // TODO: uncomment when video upload is enabled
                      // setIsVideo(false);
                    }}
                    className="absolute top-2 right-2 bg-white/90 hover:bg-white shadow-sm h-8 w-8 p-0"
                  >
                    <X className="w-4 h-4" />
                  </Button>

                  {/* Annotate */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAnnotation(true)}
                    className="w-full mt-2"
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    Annotate Image
                  </Button>
                </div>
              )
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              setOpen(false);
              resetForm();
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUploadImage}
            disabled={
              !uploadedImage || !imageTitle.trim() || isUploading
              // TODO: uncomment when video upload is enabled
              // || isProcessingVideo
            }
          >
            {isUploading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Upload Image
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Annotation modal */}
      {imagePreview && (
        <ImageAnnotationModal
          open={showAnnotation}
          onOpenChange={setShowAnnotation}
          imageUrl={imagePreview}
          imageTitle={imageTitle || "Image"}
          onSave={handleAnnotatedImageSave}
        />
      )}
    </Dialog>
  );
}
